import { areas } from '../rolesConfig/rolePermissionsConfig';
import {
  Area,
  AreaData,
  RestrictFromLevelType,
  ScopeData,
} from '../types/areas';
import { i18nInstance } from '../i18n';
import { Scope } from '../types/roles';

interface ICreateAreas {
  msid?: string;
  isAppInstalled?: (appDefId: string) => boolean;
}

export const createAreas = ({ msid , isAppInstalled = () => false }: ICreateAreas = {}): Area[] => {
  const predefinedAreas = exportFunctions.getPredefinedAreas();
  return predefinedAreas.filter(_area => shouldDisplayAreaOrScope(_area, isAppInstalled, msid))
    .map(area => ({
      title: i18nInstance.t(area.titleKey) || area.titleKey,
      scopes: createAreaScopes(area.scopes, isAppInstalled, msid),
    }));
};

export const createScopes = (msid?: string): Scope[] => {
  const createdAreas = createAreas({ msid });
  return createdAreas.map(createdArea => createdArea.scopes).flat();
};

const createAreaScopes = (scopes: ScopeData[], isAppInstalled: (appDefId: string) => boolean, msid?: string): Scope[] => {
  return scopes.filter(_scope => shouldDisplayAreaOrScope(_scope, isAppInstalled, msid))
    .map(scope => ({
      id: scope.id,
      title: i18nInstance.t(scope.titleKey) || scope.titleKey,
      description: i18nInstance.t(scope.descriptionKey) || scope.descriptionKey,
      level: scope.level,
      deprecated: scope.deprecated,
      experiments: scope.experiments,
      dependantScopes: scope.dependantScopes ?? [],
    }));
};

const shouldDisplayAreaOrScope = (areaOrScope: AreaData | ScopeData, isAppInstalled: (appDefId: string) => boolean, msid?: string): boolean => {
  if (msid) { //site level
    if (areaOrScope.restrictFromLevel === RestrictFromLevelType.SITE) { //restrict from site level
      return false;
    }
    if ((<AreaData> areaOrScope).appDefIds?.length > 0) { //if at least one of the app ids is added to the site

      const areaAppDefIds = (<AreaData> areaOrScope).appDefIds;
      return areaAppDefIds.some(isAppInstalled);
    }
    return true;
  }
  return !areaOrScope.restrictFromLevel || areaOrScope.restrictFromLevel !== 'account';
};

export function getPredefinedAreas() {
  return areas.map(_area => {
    const areasScopes = _area.scopes.map(_scope => ({ ..._scope }));
    return {
      ..._area,
      scopes: areasScopes
    };
  });
}

export function getAllScopesForTesting() {
  return areas.filter(area => !area.restrictFromLevel).reduce((scopes, _area) => scopes.concat(_area.scopes.map(scope => scope.id)), []);
}

const accountScopes = areas.map(area => area.scopes.filter(scope => scope.level === 'account'))?.flat().map(scope => scope.id);

export function isRoleContainAccountScope(roleScopes: string[]): boolean {
  return roleScopes?.some(roleScope => accountScopes.includes(roleScope));
}

const exportFunctions = {
  createAreas,
  getPredefinedAreas,
  getAllScopesForTesting,
};

export default exportFunctions;
